import React, { useEffect, useState } from "react";
import { Clock, Users } from "lucide-react";
import { formatRemaining } from "./CountdownPill";

function useParts(endTime, status) {
    const calc = () => Math.max(0, new Date(endTime).getTime() - Date.now());
    const [ms, setMs] = useState(calc);
    useEffect(() => {
        const id = setInterval(() => setMs(calc()), 1000);
        return () => clearInterval(id);
    }, [endTime]); // eslint-disable-line react-hooks/exhaustive-deps
    const ended = status !== "OPEN" || ms <= 0;
    const s = Math.floor(ms / 1000);
    return {
        ended,
        d: Math.floor(s / 86400),
        h: Math.floor((s % 86400) / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
        ms,
    };
}

const Tile = ({ value, label, urgent }) => (
    <div className="flex flex-col items-center">
        <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg border font-display text-xl font-bold tabular-nums sm:h-14 sm:w-14 sm:text-2xl ${urgent ? "border-warning/50 bg-warning/10 text-warning" : "border-border bg-background text-electric"}`}
        >
            {String(value).padStart(2, "0")}
        </div>
        <span className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
);

// Prominent timer + bidder-count block for the auction detail page.
export const AuctionStats = ({ endTime, status, count = 0, max = 0 }) => {
    const t = useParts(endTime, status);
    const urgent = !t.ended && t.ms < 10 * 60 * 1000;
    const pct = max > 0 ? Math.min(100, Math.round((count / max) * 100)) : 0;
    const nearCap = pct >= 80;

    return (
        <div className="space-y-5" data-testid="auction-stats">
            {/* Timer */}
            <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 text-electric" /> Time remaining
                </div>
                {t.ended ? (
                    <div className="rounded-lg border border-border bg-background px-3 py-2 font-display text-lg font-bold text-muted-foreground">
                        Auction ended
                    </div>
                ) : (
                    <div
                        className="flex items-center gap-2"
                        data-testid="auction-stats-timer"
                        role="status"
                        aria-label={`Time remaining: ${formatRemaining(t.ms)}`}
                    >
                        <Tile value={t.d} label="Days" urgent={urgent} />
                        <span className="font-display text-xl text-muted-foreground">:</span>
                        <Tile value={t.h} label="Hrs" urgent={urgent} />
                        <span className="font-display text-xl text-muted-foreground">:</span>
                        <Tile value={t.m} label="Min" urgent={urgent} />
                        <span className="font-display text-xl text-muted-foreground">:</span>
                        <Tile value={t.s} label="Sec" urgent={urgent} />
                    </div>
                )}
            </div>

            {/* Bidder count */}
            <div>
                <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <Users className="h-3.5 w-3.5 text-electric" /> Bidders
                    </span>
                    <span data-testid="auction-stats-bidders" className="font-display text-lg font-bold tabular-nums">
                        <span className={nearCap ? "text-brass" : "text-foreground"}>{count}</span>
                        <span className="text-muted-foreground"> / {max}</span>
                    </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                        className={`h-full rounded-full transition-[width] duration-500 ${nearCap ? "bg-brass" : "bg-electric"}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">Closes automatically when {max} unique bidders are reached.</p>
            </div>
        </div>
    );
};
